import { useCallback, useMemo, useState } from 'react';

import { ECustomerType } from '@webtrader/core/api/account/signUp';
import { useForm } from '@webtrader/core/form';
import { useTranslation } from '@webtrader/core/i18n';
import signUpService, { ISignUpSubmitParams } from '@webtrader/core/services/sign-up';
import { isValidEmail, isValidOtp, validatePassword } from '@webtrader/core/validators';

const validToSubmit = ({
  email,
  password
}: {
  email: string;
  password: string;
  onEmailValidationFail?: (message: string) => void;
  onPasswordValidationFail?: (message: string) => void;
}) => {
  if (email && !isValidEmail(email)) {
    return false;
  }

  if (password && !validatePassword(password)) {
    return false;
  }

  return true;
};

// eslint-disable-next-line max-lines-per-function
export const useSignUpForm = ({
  onSubmitSuccess,
  onSubmitFail
}: {
  onSubmitSuccess: (redirectUrl: string, message: string, title: string, email: string) => void;
  onSubmitFail: (message: string) => void;
}) => {
  const { t } = useTranslation();
  const { values, submitForm, setFieldValue } = useForm<ISignUpSubmitParams>({
    initialValues: {
      customerType: ECustomerType.INDIVIDUAL,
      countryId: undefined,
      email: '',
      password: '',
      otp: ''
    },
    onSubmit: (formValues) => {
      const { tncAcknowledged, customerType, countryId, email, password, otp } = formValues;

      if (email && customerType && countryId && password && tncAcknowledged && otp) {
        const signUp = async () => {
          if (validToSubmit({ email, password })) {
            const { success, message, params } = await signUpService.submit({
              email,
              countryId,
              password,
              otp,
              tncAcknowledged,
              customerType
            });

            if (!success) {
              onSubmitFail(message);
            } else {
              onSubmitSuccess(
                params as string,
                t(
                  'You have successfully registered your new account. Please check your email for your account details.'
                ),
                t('Success'),
                email
              );
            }
          }
        };

        signUp();
      }
    }
  });

  const [showCountryValidation, setShowCountryValidation] = useState(false);
  const [showEmailValidation, setShowEmailValidation] = useState(false);
  const [showPasswordValidation, setShowPasswordValidation] = useState(false);
  const [showOtpValidation, setShowOtpValidation] = useState(false);
  const [showAcknowledgementValidation, setShowAcknowledgementValidation] = useState(false);

  const onEmailChange = useCallback(
    (nextValue: string) => {
      setFieldValue('email', nextValue);
      const isValid = isValidEmail(nextValue);
      setShowEmailValidation(!isValid);
    },
    [setFieldValue]
  );

  const onPasswordChange = useCallback(
    (nextValue: string) => {
      setFieldValue('password', nextValue);
      const isValid = validatePassword(nextValue);
      setShowPasswordValidation(!isValid);
    },
    [setFieldValue]
  );

  const onCheckTnc = useCallback(
    (nextChecked: boolean) => {
      setFieldValue('tncAcknowledged', nextChecked);
      setShowAcknowledgementValidation(!nextChecked);
    },
    [setFieldValue]
  );

  const onCountryChange = useCallback(
    (countryId: number | undefined) => {
      setFieldValue('countryId', countryId);
      setShowCountryValidation(!countryId);
    },
    [setFieldValue]
  );

  const onCustomerTypeChange = useCallback(
    (customerType: ECustomerType) => {
      setFieldValue('customerType', customerType);
    },
    [setFieldValue]
  );

  const onOtpChange = useCallback(
    (otp: string) => {
      setFieldValue('otp', otp);
      const isValid = isValidOtp(otp);
      setShowOtpValidation(!isValid);
    },
    [setFieldValue]
  );

  const validateSubmit = useCallback(() => {
    const { customerType, email, countryId, otp, password, tncAcknowledged } = values;
    onCustomerTypeChange(customerType);
    onEmailChange(email || '');
    onCountryChange(countryId);
    onOtpChange(otp || '');
    onPasswordChange(password || '');
    onCheckTnc(tncAcknowledged || false);
  }, [onCheckTnc, onCountryChange, onCustomerTypeChange, onEmailChange, onOtpChange, onPasswordChange, values]);

  const onSubmitForm = useCallback(() => {
    validateSubmit();
    submitForm();
  }, [submitForm, validateSubmit]);

  const isDisableSubmit = useMemo(() => {
    const { customerType, email, countryId, otp, password, tncAcknowledged } = values;
    if (!customerType || !email || !countryId || !otp || !password || !tncAcknowledged) {
      return true;
    }
    if (
      showCountryValidation ||
      showEmailValidation ||
      showPasswordValidation ||
      showOtpValidation ||
      showAcknowledgementValidation
    ) {
      return true;
    }
    return false;
  }, [
    values,
    showCountryValidation,
    showEmailValidation,
    showPasswordValidation,
    showOtpValidation,
    showAcknowledgementValidation
  ]);

  return {
    values,
    onSubmitForm,
    onCheckTnc,
    onEmailChange,
    onCountryChange,
    onPasswordChange,
    onCustomerTypeChange,
    onOtpChange,
    showCountryValidation,
    showEmailValidation,
    showPasswordValidation,
    showOtpValidation,
    showAcknowledgementValidation,
    isDisableSubmit
  };
};
